import type { Broadcast, Design } from '@noorview/core';
import { appealTotals, autoInk, cornerRadius, giftLine, gridMetrics, hexToRgb, hhmm, isBroadcastLive, money, presetOf, rectToPx, rgbToHex } from '@noorview/core';
import { el } from './dom';

/**
 * Live messages and appeals drawn over the screen: the whole screen, a band along the bottom, half the screen, or in
 * place of the screen's announcement (messages) or donation (appeals) widgets. Text sizes follow each box (container
 * units), so a message reads well in a small widget and on a whole wall alike.
 */

interface Box { left: number; top: number; width: number; height: number; radius: number }

function boxesFor(b: Broadcast, d: Design): Box[] {
  const m = gridMetrics(d);
  const W = m.width, H = m.height, portrait = H > W;
  const of = (types: string[]) => d.widgets.filter((w) => !w.hidden && types.includes(w.type)).map((w) => ({ ...rectToPx(w, m), radius: cornerRadius(w, m) }));
  const band = (top = false): Box => { const h = Math.round(H * (portrait ? 0.16 : 0.2)); return { left: 0, top: top ? 0 : H - h, width: W, height: h, radius: 0 }; };
  const half = (first = false): Box => (portrait
    ? { left: 0, top: first ? 0 : Math.round(H / 2), width: W, height: Math.round(H / 2), radius: 0 }
    : { left: first ? 0 : Math.round(W / 2), top: 0, width: Math.round(W / 2), height: H, radius: 0 });
  switch (b.placement) {
    case 'full': return [{ left: 0, top: 0, width: W, height: H, radius: 0 }];
    case 'band': return [band()];
    case 'top': return [band(true)];
    case 'half': return [half()];
    case 'left': return [half(true)];
    case 'center': {
      const w = Math.round(W * (portrait ? 0.84 : 0.64)), h = Math.round(H * (portrait ? 0.4 : 0.6));
      return [{ left: Math.round((W - w) / 2), top: Math.round((H - h) / 2), width: w, height: h, radius: Math.round(Math.min(W, H) * 0.03) }];
    }
    case 'widget': {
      // One chosen widget, when the screen's design has it.
      const id = b.message?.widgetId;
      const one = id ? d.widgets.find((w) => w.id === id && !w.hidden) : undefined;
      if (one) return [{ ...rectToPx(one, m), radius: cornerRadius(one, m) }];
      // That widget isn't on the screen's live design (not sent live yet, or removed): show it in the middle.
      if (id) return boxesFor({ ...b, placement: 'center' }, d);
      const ws = b.kind === 'appeal' ? of(['donations']).concat(of(['donations']).length ? [] : of(['announcements'])) : of(['announcements']);
      return ws.length ? ws : [b.kind === 'appeal' ? half() : band()];
    }
  }
}

function messageView(b: Broadcast, box: Box): HTMLElement {
  const p = presetOf(b.message?.preset);
  const root = el('div', `nvd-bm k-${p.id}${box.width / box.height > 4.2 ? ' wide' : ''}`);
  const bg = b.message?.color || p.color;
  root.style.setProperty('--bm', bg);
  // Black or white text, whichever reads best on the colour, unless a text colour was chosen.
  root.style.color = b.message?.textColor || rgbToHex(autoInk(hexToRgb(bg)));
  const inner = el('div', 'bm-in');
  inner.append(el('div', 'bm-tag', p.label), el('div', 'bm-h', b.message?.heading || p.heading));
  if (b.message?.body) inner.append(el('div', 'bm-b', b.message.body));
  root.append(inner);
  return root;
}

function appealView(b: Broadcast, box: Box, fresh: Set<string>): HTMLElement {
  const a = b.appeal!;
  const t = appealTotals(a);
  const root = el('div', `nvd-ba${box.width / box.height > 2.4 ? ' wide' : ''}`);
  const head = el('div', 'ba-head');
  head.append(el('div', 'ba-tag', 'Live appeal'), el('div', 'ba-t', a.title || 'Appeal'));
  const amount = el('div', 'ba-amt', money(t.raised, a.currency));
  const of = el('div', 'ba-of', a.target > 0 ? `raised of ${money(a.target, a.currency)} · ${t.pct}%` : `${t.count} gift${t.count === 1 ? '' : 's'}`);
  const bar = el('div', 'ba-bar'), fill = el('i');
  fill.style.width = `${t.pct}%`;
  bar.append(fill);
  const list = el('div', 'ba-list');
  for (const g of [...a.gifts].reverse().slice(0, 4)) {
    const row = el('div', `ba-g${fresh.has(g.id) ? ' new' : ''}`, giftLine(g, a.currency));
    list.append(row);
  }
  const main = el('div', 'ba-main');
  main.append(head, amount, of, bar);
  if (a.target <= 0) bar.style.display = 'none';
  root.append(main, list);
  return root;
}

export class BroadcastLayer {
  readonly root = el('div', 'nvd-bcs');
  private list: Broadcast[] = [];
  private sig = '';
  private seenGifts = new Set<string>();
  private first = true;
  /** The time, always readable: shown whenever a message or appeal covers every clock on the screen. */
  private clock = el('div', 'nvd-bc-clock');
  private needClock = false;

  set(list: readonly Broadcast[]) { this.list = [...list]; this.sig = ''; }

  /** Redraws only when what's live (or the design's widget boxes) changed. */
  tick(now: Date, design: Design | null, screenId?: string, tz = 'Europe/London') {
    if (!design) return;
    if (this.needClock) this.clock.textContent = hhmm(now, tz);
    const live = this.list.filter((b) => b.kind !== 'khutbah' && isBroadcastLive(b, now, screenId)).sort((x, y) => (x.kind === y.kind ? x.startsAt.localeCompare(y.startsAt) : x.kind === 'appeal' ? -1 : 1));
    const sig = JSON.stringify([live.map((b) => [b.id, b.placement, b.message, b.appeal]), design.aspect, live.some((b) => b.placement === 'widget') ? design.widgets.map((w) => [w.type, w.x, w.y, w.w, w.h, w.hidden, w.radius]) : 0]);
    if (sig === this.sig) return;
    this.sig = sig;
    this.needClock = false;
    const fresh = new Set<string>();
    for (const b of live) for (const g of b.appeal?.gifts ?? []) { if (!this.seenGifts.has(g.id) && !this.first) fresh.add(g.id); this.seenGifts.add(g.id); }
    this.first = false;
    this.root.replaceChildren();
    const covering: Box[] = [];
    for (const b of live) {
      for (const box of boxesFor(b, design)) {
        covering.push(box);
        const v = b.kind === 'appeal' && b.appeal ? appealView(b, box, fresh) : messageView(b, box);
        Object.assign(v.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px`, borderRadius: `${box.radius}px` });
        v.classList.add(`p-${b.placement}`);
        // Emergencies show even during salah blanking.
        if (b.message?.preset === 'emergency') v.classList.add('urgent');
        this.root.append(v);
      }
    }
    // Is any clock on the screen still mostly in view? If not, show the time in the top corner.
    if (covering.length) {
      const m = gridMetrics(design);
      const clocks = design.widgets.filter((w) => !w.hidden && w.type === 'clock').map((w) => rectToPx(w, m));
      const seen = (r: { left: number; top: number; width: number; height: number }) => {
        const area = r.width * r.height;
        const hidden = covering.reduce((s, b) => s + Math.max(0, Math.min(r.left + r.width, b.left + b.width) - Math.max(r.left, b.left)) * Math.max(0, Math.min(r.top + r.height, b.top + b.height) - Math.max(r.top, b.top)), 0);
        return hidden < area * 0.5;
      };
      if (!clocks.some(seen)) {
        this.needClock = true;
        const s = Math.round(Math.min(m.width, m.height) * 0.06);
        Object.assign(this.clock.style, { fontSize: `${s}px`, top: `${Math.round(s * 0.5)}px`, right: `${Math.round(s * 0.6)}px` });
        this.clock.textContent = hhmm(now, tz);
        this.root.append(this.clock);
      }
    }
  }
}

export const BROADCAST_CSS = `
.nvd-bcs{position:absolute;inset:0;z-index:900;pointer-events:none}
.nvd-alert{position:absolute;inset:0;container-type:size;border-radius:var(--r,0);overflow:hidden}
/* As a widget design, the message takes the widget's own colour and text colour when they're set. */
.has-fill .nvd-alert{background:var(--f)}
.nvd-w .nvd-alert{font-family:inherit}
/* As a widget design the text follows the widget's text size and the Content size slider, like every widget. */
.nvd-w .nvd-alert{position:relative;inset:auto;flex:1;min-height:0;width:100%;box-sizing:border-box;padding:calc(var(--fs)*var(--k)*.5) calc(var(--fs)*var(--k)*.6)}
.nvd-w .nvd-alert .bm-in{gap:calc(var(--fs)*var(--k)*.14)}
.nvd-w .nvd-alert .bm-tag{font-size:calc(var(--fs)*var(--k)*.36)}
.nvd-w .nvd-alert .bm-h{font-size:calc(var(--fs)*var(--k)*1.05);overflow-wrap:normal}
.nvd-w .nvd-alert .bm-b{overflow-wrap:normal}
.nvd-w .nvd-alert .bm-b{font-size:calc(var(--fs)*var(--k)*.48)}
.nvd-w .nvd-alert.wide{padding:0 calc(var(--fs)*var(--k)*.6)}
.nvd-w .nvd-alert.wide .bm-in{gap:calc(var(--fs)*var(--k)*.4)}
.nvd-w .nvd-alert.wide .bm-tag{font-size:calc(var(--fs)*var(--k)*.42);padding-right:calc(var(--fs)*var(--k)*.4);border-right-width:max(1px,calc(var(--fs)*var(--k)*.04))}
.nvd-w .nvd-alert.wide .bm-h{font-size:calc(var(--fs)*var(--k)*.95)}
.nvd-w .nvd-alert.wide .bm-b{font-size:calc(var(--fs)*var(--k)*.5)}
.nvd-alert.wide .bm-tag{border-right-color:color-mix(in srgb,currentColor 35%,transparent)}
.nvd-bcs>div{position:absolute;overflow:hidden;container-type:size;box-sizing:border-box;animation:nvd-bc-in .5s ease both}
.nvd-bcs>.urgent{z-index:2}
.nvd-bcs>.p-center{box-shadow:0 24px 80px rgba(0,0,0,.45),0 0 0 1px rgba(255,255,255,.08)}
.nvd-bcs>.nvd-bc-clock{position:absolute;left:auto;bottom:auto;z-index:5;color:#fff;font-family:var(--nvd-ui-font,"SF Pro Display",-apple-system,"Inter Variable",sans-serif);font-weight:650;font-variant-numeric:tabular-nums;letter-spacing:-.01em;line-height:1;text-shadow:0 1px 3px rgba(0,0,0,.45),0 0 18px rgba(0,0,0,.35);overflow:visible;container-type:normal;animation:none}
@keyframes nvd-bc-in{from{opacity:0;transform:scale(.985)}to{opacity:1;transform:none}}
.nvd-bm{background:var(--bm);color:#fff;display:flex;align-items:center;justify-content:center;text-align:center;padding:6cqmin 7cqmin;font-family:var(--nvd-ui-font,"SF Pro Display",-apple-system,"Inter Variable",sans-serif)}
.nvd-bm .bm-in{display:flex;flex-direction:column;align-items:center;gap:2.4cqmin;max-width:100%;min-width:0}
.nvd-bm .bm-tag{font-size:6.4cqmin;font-weight:600;letter-spacing:0;opacity:.8}
.nvd-bm .bm-h{font-size:17cqmin;font-weight:700;line-height:1.04;letter-spacing:-.025em;overflow-wrap:anywhere}
.nvd-bm .bm-b{font-size:8cqmin;font-weight:500;line-height:1.3;opacity:.9;display:-webkit-box;-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden}
.nvd-bm.p-full .bm-h{font-size:14cqmin}.nvd-bm.p-full .bm-b{font-size:6cqmin}.nvd-bm.p-full .bm-tag{font-size:3.6cqmin}
.nvd-bm:is(.p-band,.p-top):not(.wide){padding:3cqmin 5cqw}.nvd-bm:is(.p-band,.p-top):not(.wide) .bm-in{gap:2cqmin}.nvd-bm:is(.p-band,.p-top):not(.wide) .bm-h{font-size:28cqmin}.nvd-bm:is(.p-band,.p-top):not(.wide) .bm-b{font-size:14cqmin;-webkit-line-clamp:2}.nvd-bm:is(.p-band,.p-top):not(.wide) .bm-tag{font-size:10cqmin}
.nvd-bm.wide{padding:0 4cqw;justify-content:flex-start;text-align:left}
.nvd-bm.wide .bm-in{flex-direction:row;align-items:center;gap:3cqw}
.nvd-bm.wide .bm-tag{font-size:18cqh;white-space:nowrap;padding-right:3cqw;border-right:max(1px,1.2cqh) solid rgba(255,255,255,.35)}
.nvd-bm.wide .bm-in{width:100%}.nvd-bm.wide .bm-h{font-size:34cqh;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex:0 1 auto;min-width:0}.nvd-bm.wide .bm-b{flex:1 1 40%;min-width:0}
.nvd-bm.wide .bm-b{font-size:22cqh;-webkit-line-clamp:2;text-align:left}
.nvd-bm.k-emergency{animation:nvd-bc-in .5s ease both,nvd-pulse 2.4s ease-in-out infinite}
@keyframes nvd-pulse{0%,100%{filter:none}50%{filter:brightness(1.18)}}
.nvd-ba{background:radial-gradient(120% 120% at 80% -10%,#1d3557 0%,#050b16 70%);color:#fff;display:flex;flex-direction:column;justify-content:center;gap:4cqmin;padding:6cqmin 7cqmin;font-family:var(--nvd-ui-font,"SF Pro Display",-apple-system,"Inter Variable",sans-serif)}
.nvd-ba .ba-main{display:flex;flex-direction:column;gap:2.4cqmin;min-width:0}
.nvd-ba .ba-tag{font-size:4.4cqmin;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#FF375F}
.nvd-ba .ba-t{font-size:7cqmin;font-weight:650;line-height:1.15;overflow-wrap:anywhere}
.nvd-ba .ba-amt{font-size:19cqmin;font-weight:800;line-height:1;font-variant-numeric:tabular-nums;letter-spacing:-.02em}
.nvd-ba .ba-of{font-size:5cqmin;opacity:.8}
.nvd-ba .ba-bar{height:3.2cqmin;border-radius:2cqmin;background:rgba(255,255,255,.14);overflow:hidden}
.nvd-ba .ba-bar>i{display:block;height:100%;border-radius:inherit;background:linear-gradient(90deg,#FF375F,#FF9F0A);transition:width 1.2s cubic-bezier(.2,.8,.2,1)}
.nvd-ba .ba-list{display:flex;flex-direction:column;gap:1.6cqmin;min-height:0;overflow:hidden}
.nvd-ba .ba-g{font-size:4.6cqmin;padding:1.6cqmin 3cqmin;border-radius:2cqmin;background:rgba(255,255,255,.08);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.nvd-ba .ba-g.new{animation:nvd-gift 2.4s ease both}
@keyframes nvd-gift{0%{background:rgba(255,159,10,.7);transform:scale(1.04)}100%{background:rgba(255,255,255,.08);transform:none}}
.nvd-ba.wide{flex-direction:row;align-items:center;gap:5cqw}
.nvd-ba.wide .ba-main{flex:1}
.nvd-ba.wide .ba-list{flex:0 0 38%}
.nvd-ba.wide .ba-amt{font-size:30cqh}.nvd-ba.wide .ba-t{font-size:11cqh}.nvd-ba.wide .ba-of,.nvd-ba.wide .ba-g{font-size:8cqh}.nvd-ba.wide .ba-tag{font-size:7cqh}.nvd-ba.wide .ba-bar{height:5cqh}
@media (prefers-reduced-motion:reduce){.nvd-bcs>div,.nvd-bm.k-emergency,.nvd-ba .ba-g.new{animation:none}}
`;
